import React, { useState, useEffect } from 'react';
import {
  useMaterialReactTable,
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_Row,
  MRT_Cell,
  MRT_RowData,
  MRT_RowSelectionState,
} from 'material-react-table';
import { useNavigate } from 'react-router-dom';
import { Box, Button, IconButton, ThemeProvider, Typography, Checkbox, CircularProgress } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete'; // Import DeleteIcon
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Tabletheme } from '../../../styles/table.style';

export interface EditableColumnDef<T extends MRT_RowData> extends MRT_ColumnDef<T> {
  renderEditCell?: (params: {
    value: any;
    onChange: (value: any) => void;
    rowIndex: number;
  }) => React.ReactNode;
}

export interface EditableTableProps<T extends MRT_RowData> {
  data: T[];
  columns: EditableColumnDef<T>[];
  onSaveRow: (updatedRow: T) => void;
  onCellChange: (rowIndex: number, columnId: string, value: any) => void;
  editingRowIndex: number | null;
  onCancelEdit: (rowIndex: number) => void; // Pass rowIndex
  onEditRow: (rowIndex: number) => void;
  onDeleteRow: (rowIndex: number) => void; // Added onDeleteRow prop
  enableEditing?: boolean;
  cellHeight?: number;
  hideDelete?: boolean;
  alwaysEditableRows?: string[];
  editingRowIndices?: number[];
  customeEmptyRecordText?: boolean;
  customeEmptyRecordComponent?: React.ReactNode;
  formtype?: string;
  TE_ID?: string;
  isSaving?: boolean;
  showSuccessIcon?: boolean; // New parameter
  enablePagination?: boolean;
  // Row selection props
  enableRowSelection?: boolean;
  selectedRowIndices?: number[];
  onSelectionChange?: (selectedIndices: number[]) => void;
}

export const EditableTable = <T extends MRT_RowData>({
  data,
  columns,
  onSaveRow,
  onCellChange,
  editingRowIndex,
  onCancelEdit,
  onEditRow,
  onDeleteRow,
  enableEditing = true,
  cellHeight = 60,
  hideDelete = false,
  alwaysEditableRows = [],
  editingRowIndices = [],
  customeEmptyRecordText = false,
  customeEmptyRecordComponent,
  formtype = '',
  TE_ID = '',
  isSaving = false,
  showSuccessIcon = false, // New parameter with default value
  enablePagination = false,
  // Row selection props
  enableRowSelection = false,
  selectedRowIndices = [],
  onSelectionChange
}: EditableTableProps<T>) => {
  const navigate = useNavigate();
  
  // Row selection state management
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  
  // Initialize row selection from props
  useEffect(() => {
    if (enableRowSelection && selectedRowIndices.length > 0) {
      const initialSelection: MRT_RowSelectionState = {};
      selectedRowIndices.forEach(index => {
        initialSelection[index.toString()] = true;
      });
      setRowSelection(initialSelection);
    }
  }, [enableRowSelection, selectedRowIndices]);
  
  // Handle row selection change
  const handleRowSelectionChange = (updaterOrValue: any) => {
    const newSelection = typeof updaterOrValue === 'function' 
      ? updaterOrValue(rowSelection) 
      : updaterOrValue;
    
    setRowSelection(newSelection);
    
    // Convert to array of selected indices and call callback
    if (onSelectionChange) {
      const selectedIndices = Object.keys(newSelection)
        .filter(key => newSelection[key])
        .map(key => Number(key));
      onSelectionChange(selectedIndices);
    }
  };
  
  // Check if a row is selected
  const isRowSelected = (rowIndex: number) => {
    return enableRowSelection ? rowSelection[rowIndex.toString()] || false : true;
  };
  
  const table = useMaterialReactTable({
    data,
    enablePagination,
    enableRowSelection: enableRowSelection,
    getRowId: (row, index) => index.toString(),
    onRowSelectionChange: handleRowSelectionChange,
    state: {
      rowSelection: rowSelection,
    },
    columns: columns.map((col) => ({
      ...col,
      Cell: ({ cell, row }: { cell: MRT_Cell<T>; row: MRT_Row<T> }) => {
        const isEditing = editingRowIndex === row.index;
        const cellValue = cell.getValue() as any;
        const accessorKey = col.accessorKey as string;
        const isCellEmpty = cellValue === null || cellValue === undefined || cellValue === '' || (Array.isArray(cellValue) && cellValue.length === 0);

        if ((isEditing && col.renderEditCell) || (alwaysEditableRows.includes(accessorKey) && col.renderEditCell && isCellEmpty)) {
          return col.renderEditCell({
            value: cellValue,
            onChange: (newValue: any) =>
              onCellChange(row.index, col.accessorKey as string, newValue),
            rowIndex: row.index,
          });
        }
        if (Array.isArray(cellValue)) {
          return <span>{cellValue.join(', ')}</span>;
        }
        return <span>{cellValue}</span>;
      },
    })),
    muiTableHeadCellProps: ({ column }) => ({
      sx: {
        color: '#ffffff', // Direct colors
        backgroundColor: '#2C4748',
        '& .MuiTableSortLabel-root': {
          color: '#fff', // text color of the label
          '&:hover': {
            color: '#fff',
          },
          '&.Mui-active': {
            color: '#fff',
          },
        },
        '& .MuiTableSortLabel-icon': {
          color: '#fff !important', // force the icon to white
        },
      },
    }),
    enableTopToolbar: false,
    enableBottomToolbar: false,
    muiTableBodyCellProps: {
      sx: {
        height: `${cellHeight}px`, // Adjust height as needed
        padding: '16px', // Adjust padding if necessary
      },
    },
    renderRowActions: ({ row }: { row: MRT_Row<T> }) => {
      const isRowSelectedForEditing = isRowSelected(row.index);
      const isDisabled = enableRowSelection && !isRowSelectedForEditing;
      
      return editingRowIndices.includes(row.index) ? (
        <Box display="flex" mb={2}>
          <IconButton onClick={() => onSaveRow(data[row.index])} color="primary" disabled={isSaving}>
            {isSaving ? <CircularProgress size={24} color="inherit" /> : <SaveIcon />}
          </IconButton>
          <IconButton onClick={() => onCancelEdit(row.index)} color="secondary" disabled={isSaving}> {/* Pass row index */}
            <CancelIcon />
          </IconButton>
        </Box>
      ) : editingRowIndex === row.index ? (
        <Box display="flex" mb={2}>
          <IconButton onClick={() => onSaveRow(data[row.index])} color="primary" style={{ color: '#006EB3' }} disabled={isSaving}>
            {isSaving ? <CircularProgress size={24} color="inherit" /> : <SaveIcon />}
          </IconButton>
          <IconButton onClick={() => onCancelEdit(row.index)} color="secondary" style={{ color: 'rgb(244, 67, 54)' }} disabled={isSaving}> {/* Pass row index */}
            <CancelIcon />
          </IconButton>
        </Box>
      ) : showSuccessIcon ? (
        <Box display="flex">
          <IconButton disabled color="success" style={{ color: '#4CAF50' }}>
            <CheckCircleIcon />
          </IconButton>
        </Box>
      ) : (
        <Box display="flex">
          <IconButton 
            disabled={(editingRowIndex !== null && editingRowIndex > -1) || isDisabled || isSaving}
            onClick={() => onEditRow(row.index)} 
            color="default"
            style={{ opacity: isDisabled ? 0.5 : 1 }}
          >
            <EditIcon />
          </IconButton>
          {
            !hideDelete ? (
              <IconButton 
                disabled={(editingRowIndex !== null && editingRowIndex > -1) || isDisabled || isSaving}
                onClick={() => onDeleteRow(row.index)} 
                color="error" 
                style={{ color: 'rgb(244, 67, 54)', opacity: isDisabled ? 0.5 : 1 }}
              >
                <DeleteIcon />
              </IconButton>
            ) : null
          }
        </Box>
      );
    },
    positionActionsColumn: 'last',
    muiTableBodyRowProps: ({ row }: { row: MRT_Row<T> }) => {
      const isRowSelectedForEditing = isRowSelected(row.index);
      const isDisabled = enableRowSelection && !isRowSelectedForEditing;

      let rowBackgroundColor = 'inherit';
      if (editingRowIndex === row.index) {
        rowBackgroundColor = 'rgba(0, 0, 0, 0.04)'; // For the currently editing row
      } else if (enableRowSelection && row.getIsSelected()) {
        rowBackgroundColor = '#ffffff !important'; // Force white background for selected rows
      }
      
      return {
        sx: {
          backgroundColor: rowBackgroundColor,
          borderBottom: '1px solid rgba(224, 224, 224, 1)',
          opacity: isDisabled ? 0.5 : 1,
          '&:hover': {
            backgroundColor: isDisabled ? 'inherit' : 'rgba(0, 0, 0, 0.02)',
          },
          // Override MRT's default selected row styling
          '&.Mui-selected': {
            backgroundColor: '#ffffff !important',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.02) !important',
            },
          },
        },
      };
    },
    enableEditing: enableEditing,
    renderEmptyRowsFallback: ({ table }) => {
      return customeEmptyRecordText ? customeEmptyRecordComponent : null
    },
  });

  return (
    <ThemeProvider theme={Tabletheme}>
      <MaterialReactTable table={table} />
    </ThemeProvider>
  );
};
